import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import * as Icons from 'react-feather'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { CreateAlert } from '../common-alerts/SKAlerts'
import SKAlerts from '../common-alerts/SKAlerts'
import FinanceRegisterModel from '../../pages/finance-register/models/FinanceRegisterModel'
import { logAuditEvent } from '../../utils/auditLogger'
import { generateFinanceRegisterPdf } from '../../pages/finance-register/utils/financeRegisterPdf'
import { ReactComponent as SortArrow } from '../img/chevron-up-down.svg'

const EmailMessage = forwardRef(({ FinanceRegisterData = new FinanceRegisterModel(), onCancel = () => {}, onSent = () => {}, moduleName = 'Finance Register' }, ref) => {
  const [activeTab, setActiveTab] = useState('email')
  const [fromEmail, setFromEmail] = useState('')
  const [toEmail, setToEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [fromMobile, setFromMobile] = useState('')
  const [toMobile, setToMobile] = useState('')
  const [whatsAppMessage, setWhatsAppMessage] = useState('')
  const [files, setFiles] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const [includePdf, setIncludePdf] = useState(true)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [alert, setAlert] = useState(null)
  const pdfCacheRef = useRef(null)

  useEffect(() => {
    const title = FinanceRegisterData?.CustomerName || FinanceRegisterData?.SalesNumber || FinanceRegisterData?.FinanceRegisterId
    setSubject(`Finance Register Documents - ${title || ''}`.trim())
  }, [FinanceRegisterData?.CustomerName, FinanceRegisterData?.SalesNumber, FinanceRegisterData?.FinanceRegisterId])

  useEffect(() => {
    const defaultMobile = FinanceRegisterData?.MobileNo || FinanceRegisterData?.MobileNumber || ''
    if (defaultMobile && !toMobile) {
      setToMobile(defaultMobile)
    }
  }, [FinanceRegisterData?.MobileNo, FinanceRegisterData?.MobileNumber, toMobile])

  useEffect(() => {
    const lines = [
      'Finance Register Details',
      FinanceRegisterData?.CustomerName ? `Customer: ${FinanceRegisterData.CustomerName}` : '',
      FinanceRegisterData?.SalesNumber ? `Sales#: ${FinanceRegisterData.SalesNumber}` : ''
    ].filter(Boolean)
    if (!whatsAppMessage) {
      setWhatsAppMessage(lines.join('\n'))
    }
  }, [FinanceRegisterData?.CustomerName, FinanceRegisterData?.SalesNumber, whatsAppMessage])

  useEffect(() => {
    const fetchData = async () => {
      if (!FinanceRegisterData) return
      setLoading(true)
      try {
        const [fromValue, partnerEmail, fileList, fromPhone] = await Promise.all([
          fetchFromEmail(),
          fetchFinancePartnerEmail(FinanceRegisterData?.FinancePartnerId),
          fetchCustomerFiles(FinanceRegisterData?.CustomerId),
          fetchFromMobile()
        ])
        setFromEmail(fromValue || '')
        setToEmail(partnerEmail || '')
        setFiles(fileList)
        setSelectedFileIds(fileList.filter((f) => f.FileLibraryId).map((f) => f.FileLibraryId))
        if (fromPhone && !fromMobile) {
          setFromMobile(fromPhone)
        }
      } catch (error) {
        console.error(error)
        CreateAlert('Unable to load email defaults', 'Error', setAlert)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [FinanceRegisterData, fromMobile])

  useImperativeHandle(ref, () => ({
    submit: () => (activeTab === 'whatsapp' ? handleSendWhatsApp() : handleSendEmail())
  }))

  const fetchFromEmail = async () => {
    try {
      const res = await axios.get(getApiBaseUrl() + '/clientsettings/getall')
      const payload = res?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const match = (list || []).find((item) => (item?.SettingParameter || '').toLowerCase().includes('email'))
      return match?.Value || ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const fetchFromMobile = async () => {
    try {
      const res = await axios.get(getApiBaseUrl() + '/clientsettings/getall')
      const payload = res?.data?.data
      const list = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      const match = (list || []).find((item) => {
        const key = (item?.SettingParameter || '').toLowerCase()
        return key.includes('mobile') || key.includes('phone')
      })
      return match?.Value || ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const fetchFinancePartnerEmail = async (financePartnerId) => {
    if (!financePartnerId) return ''
    try {
      const res = await axios.get(getApiBaseUrl() + '/financepartner/getbyid', {
        params: { FinancePartnerId: financePartnerId }
      })
      const payload = res?.data?.data
      const row = Array.isArray(payload) ? payload[0] : Array.isArray(payload?.[0]) ? payload[0][0] : payload
      return row?.EmailId || ''
    } catch (error) {
      console.error(error)
      return ''
    }
  }

  const fetchCustomerFiles = async (customerId) => {
    if (!customerId) return []
    try {
      const res = await axios.post(getApiBaseUrl() + '/file/list', {
        entityId: customerId,
        entityType: 'customer'
      })
      const payload = res?.data?.data
      const normalized = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.[0])
          ? payload[0]
          : []
      return normalized
    } catch (error) {
      console.error(error)
      return []
    }
  }

  const toggleFileSelection = useCallback((fileId, checked) => {
    setSelectedFileIds((prev) => {
      if (checked) return Array.from(new Set([...(prev || []), fileId]))
      return (prev || []).filter((id) => id !== fileId)
    })
  }, [])

  const buildPdf = useCallback(async () => {
    if (pdfCacheRef.current) return pdfCacheRef.current
    const titles = files.map((f) => f.FileTitle || f.OriginalName).filter(Boolean)
    const { dataUri, base64 } = await generateFinanceRegisterPdf(FinanceRegisterData, titles)
    const filename = `FinanceRegister-${FinanceRegisterData?.SalesNumber || FinanceRegisterData?.FinanceRegisterId || 'Details'}.pdf`
    const payload = { filename, base64, dataUri }
    pdfCacheRef.current = payload
    return payload
  }, [FinanceRegisterData, files])

  const handlePreviewPdf = useCallback(async () => {
    try {
      const pdf = await buildPdf()
      if (!pdf?.dataUri) return
      const previewWindow = window.open('', '_blank')
      if (previewWindow) {
        const dataPart = pdf.base64 || pdf.dataUri.split(',')[1] || ''
        const byteCharacters = atob(dataPart)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })
        const blobUrl = URL.createObjectURL(blob)
        previewWindow.document.title = 'Finance Register PDF'
        previewWindow.document.body.style.margin = '0'
        previewWindow.document.body.innerHTML = `

          <iframe
            src="${blobUrl}"
            style="border:0; width:100%; height:100vh;"
            title="Finance Register PDF"
          ></iframe>
        `
      }
    } catch (error) {
      console.error(error)
      CreateAlert('Unable to generate PDF', 'Error', setAlert)
    }
  }, [buildPdf])

  const handleSendEmail = async () => {
    if (sending) return
    if (!toEmail) {
      CreateAlert('Please provide recipient email', 'Warning', setAlert)
      return
    }
    setSending(true)
    try {
      const pdfAttachment = includePdf ? await buildPdf() : null
      const payload = {
        FinanceRegisterId: FinanceRegisterData?.FinanceRegisterId,
        FinancePartnerId: FinanceRegisterData?.FinancePartnerId,
        fromEmail,
        toEmail,
        subject: subject || 'Finance Register Documents',
        description,
        attachmentFileLibraryIds: selectedFileIds,
        pdfAttachment: pdfAttachment
          ? {
            filename: pdfAttachment.filename,
            base64: pdfAttachment.base64
          }
          : null,
        customerName: FinanceRegisterData?.CustomerName || '',
        salesNumber: FinanceRegisterData?.SalesNumber || ''
      }

      await axios.post(getApiBaseUrl() + '/financeregister/sendemail', payload)
      CreateAlert('Email sent successfully', 'Success', setAlert)
      logAuditEvent(moduleName, 'Email Send', JSON.stringify({ FinanceRegisterId: FinanceRegisterData?.FinanceRegisterId, toEmail }))
      onSent()
    } catch (error) {
      console.error(error)
      const message = error?.response?.data?.message || 'Unable to send email'
      CreateAlert(message, 'Error', setAlert)
    } finally {
      setSending(false)
    }
  }

  const handleSendWhatsApp = async () => {
    if (sending) return
    if (!toMobile) {
      CreateAlert('Please provide recipient mobile number', 'Warning', setAlert)
      return
    }
    if (!whatsAppMessage) {
      CreateAlert('Please provide a message', 'Warning', setAlert)
      return
    }
    setSending(true)
    try {
      const pdfAttachment = includePdf ? await buildPdf() : null
      const payload = {
        FinanceRegisterId: FinanceRegisterData?.FinanceRegisterId,
        FinancePartnerId: FinanceRegisterData?.FinancePartnerId,
        fromMobile,
        toMobile,
        message: whatsAppMessage,
        attachmentFileLibraryIds: selectedFileIds,
        pdfAttachment: pdfAttachment
          ? {
            filename: pdfAttachment.filename,
            base64: pdfAttachment.base64
          }
          : null,
        customerName: FinanceRegisterData?.CustomerName || '',
        salesNumber: FinanceRegisterData?.SalesNumber || ''
      }

      await axios.post(getApiBaseUrl() + '/whatsapp/send', payload)
      CreateAlert('WhatsApp message sent successfully', 'Success', setAlert)
      logAuditEvent(moduleName, 'WhatsApp Send', JSON.stringify({ FinanceRegisterId: FinanceRegisterData?.FinanceRegisterId, toMobile }))
      onSent()
    } catch (error) {
      console.error(error)
      const message = error?.response?.data?.message || 'Unable to send WhatsApp message'
      CreateAlert(message, 'Error', setAlert)
    } finally {
      setSending(false)
    }
  }

  const handlePreviewFile = useCallback((file) => {
    if (!file?.FileLibraryId) return
    const url = `${getApiBaseUrl()}/file/download/${file.FileLibraryId}?inline=1`
    window.open(url, '_blank')
  }, [])

  const allSelected = useMemo(() => {
    const ids = files.filter((f) => f.FileLibraryId).map((f) => f.FileLibraryId)
    if (!ids.length) return false
    return ids.every((id) => selectedFileIds.includes(id))
  }, [files, selectedFileIds])

  const toggleAll = useCallback((checked) => {
    if (checked) {
      const ids = files.filter((f) => f.FileLibraryId).map((f) => f.FileLibraryId)
      setSelectedFileIds(ids)
    } else {
      setSelectedFileIds([])
    }
  }, [files])

  const attachmentRows = useMemo(() => {
    const fileRows = (files || []).map((file) => ({
      ...file,
      _rowType: 'file',
      _rowId: `file-${file.FileLibraryId || file.OriginalName}`
    }))
    const pdfRow = {
      _rowType: 'pdf',
      _rowId: 'pdf-row',
      FileTitle: 'Generated PDF (Finance Register)',
      OriginalName: 'Finance Register PDF',
      FileCategory: 'PDF'
    }
    return [...fileRows, pdfRow]
  }, [files])

  const columns = useMemo(() => ([
    {
      name: '',
      width: '70px',
      cell: (row) => {
        if (row._rowType === 'pdf') {
          return (
            <input
              type="checkbox"
              className="form-check-input"
              checked={includePdf}
              onChange={(e) => setIncludePdf(e.target.checked)}
            />
          )
        }
        return (
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedFileIds.includes(row.FileLibraryId)}
            onChange={(e) => toggleFileSelection(row.FileLibraryId, e.target.checked)}
          />
        )
      }
    },
    {
      name: 'Title',
      selector: (row) => row.FileTitle || '-',
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'File Name',
      selector: (row) => row.OriginalName || '-',
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'Category',
      selector: (row) => row.FileCategory || '-',
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: 'Actions',
      cell: (row) => {
        if (row._rowType === 'pdf') {
          return (
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to=""
                onClick={handlePreviewPdf}
              >
                <Icons.Eye data-feather="eye" className="action-eye" />
              </Link>
            </div>
          )
        }
        return row.FileLibraryId ? (
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to=""
              onClick={() => handlePreviewFile(row)}
            >
              <Icons.Eye data-feather="eye" className="action-eye" />
            </Link>
          </div>
        ) : null
      },
      width: '80px'
    }
  ]), [handlePreviewFile, handlePreviewPdf, includePdf, selectedFileIds, toggleFileSelection])

  return (
    <div>
      <SKAlerts alert={alert} />
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
            role="tab"
            aria-selected={activeTab === 'email'}
          >
            Email
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp')}
            role="tab"
            aria-selected={activeTab === 'whatsapp'}
          >
            WhatsApp
          </button>
        </li>
      </ul>

      <div className="tab-content">
        {activeTab === 'email' && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">From</label>
                <input type="email" className="form-control" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="sender@example.com" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">To</label>
                <input type="email" className="form-control" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="finance-partner@example.com" />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Subject</label>
                <input type="text" className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Add a message for the finance partner" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'whatsapp' && (
          <div className="tab-pane fade show active" role="tabpanel">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">From Mobile</label>
                <input type="text" className="form-control" value={fromMobile} onChange={(e) => setFromMobile(e.target.value)} placeholder="Sender mobile" />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">To Mobile</label>
                <input type="text" className="form-control" value={toMobile} onChange={(e) => setToMobile(e.target.value)} placeholder="Recipient mobile" />
              </div>
              <div className="col-12 mb-3">
                <label className="form-label">Message</label>
                <textarea className="form-control" rows={4} value={whatsAppMessage} onChange={(e) => setWhatsAppMessage(e.target.value)} placeholder="Type your message" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Attachments</h6>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" id="selectAllAttachments" checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} />
          <label className="form-check-label" htmlFor="selectAllAttachments">Select All</label>
        </div>
      </div>

      <div className="table-responsive">
        <DataTable
          columns={columns}
          data={attachmentRows}
          noDataComponent={<div className="py-3 text-center text-muted">No files</div>}
          dense
          persistTableHead
          highlightOnHover
          striped
          sortIcon={<SortArrow />}
        />
      </div>

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-cancel" type="button" onClick={onCancel} disabled={sending}>Cancel</button>
        {activeTab === 'email' ? (
          <button className="btn btn-submit" type="button" onClick={handleSendEmail} disabled={sending || loading}>
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        ) : (
          <button className="btn btn-submit" type="button" onClick={handleSendWhatsApp} disabled={sending || loading}>
            {sending ? 'Sending…' : 'Send WhatsApp'}
          </button>
        )}
      </div>
    </div>
  )
})

EmailMessage.displayName = 'EmailMessage'

export default EmailMessage
